"use client";

import { useState, useEffect, useMemo } from "react";

import { useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import FilterSearch from "../../../../components/FilterSearch(name)";
import FilterButtons from "../../../../components/universal-filter-tabs";

import Button from "../../../../components/Button";
import styles from "../css/splitProfileTable.module.css";
import { listSplitCandidates } from "../../../../api/profile/split_profile";

export default function SplitProfileTable() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const rowsPerPage = 6;

  const fetchAllPatients = async () => {
    try {
      const aggregated = [];
      const pageSizeServer = 200;
      let page = 1;
      let total = 0;

      for (;;) {
        const { data, total: serverTotal } = await listSplitCandidates({ search: "", page, pageSize: pageSizeServer });
        const items = Array.isArray(data) ? data : [];
        aggregated.push(...items);
        total = Number(serverTotal || 0);
        if (!items.length || aggregated.length >= total) break;
        page += 1;
      }

      const withBeneficiaries = aggregated.filter((row) => Number(row?.beneficiaryCount || 0) > 0);
      setTableRows(withBeneficiaries);
      setTotalCount(withBeneficiaries.length);
    } catch (_) {
      setTableRows([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    fetchAllPatients();
  }, []);

  const filteredRows = useMemo(
    () => {
      const q = (searchTerm || "").toLowerCase().trim();

      const bySearch = (tableRows || []).filter((row) => {
        if (!q) return true;
        const surname = (row.surname || "").toLowerCase();
        const initial = (row.initial || "").toLowerCase();
        const title = (row.title || "").toLowerCase();
        const address = (row.address || "").toLowerCase();
        return (
          surname.includes(q) ||
          initial.includes(q) ||
          title.includes(q) ||
          address.includes(q)
        );
      });

      return bySearch.filter((row) => {
        const enabled = row?.profile ? row.profile.enabled !== false : true;
        if (statusFilter === "Active") return enabled;
        if (statusFilter === "Inactive") return !enabled;
        return true;
      });
    },
    [tableRows, searchTerm, statusFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const renderCell = (value, row) => {
    const enabled = row?.profile ? row.profile.enabled !== false : true;
    const className = enabled ? undefined : styles.disabledCellText;
    return <span className={className}>{value}</span>;
  };

  const columns = [
    { header: "Surname", accessor: "surname", render: (value, row) => renderCell(value, row) },
    { header: "Initial", accessor: "initial", render: (value, row) => renderCell(value, row) },
    { header: "Title", accessor: "title", render: (value, row) => renderCell(value, row) },
    { header: "Address", accessor: "address", render: (value, row) => renderCell(value, row) },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => {
        const enabled = row?.profile ? row.profile.enabled !== false : true;
        const className = enabled ? undefined : styles.disabledCellText;
        return <span className={className}>{enabled ? "Enabled" : "Disabled"}</span>;
      },
    },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => {
        const enabled = row?.profile ? row.profile.enabled !== false : true;
        if (!enabled) {
          return <div className={styles.actionCell} />;
        }

        return (
          <div className={styles.actionCell}>
            <Button
              variant="primary"
              onClick={() => handleSplit(row)}
            >
              Split
            </Button>
          </div>
        );
      },
    },
  ];

  const handleSplit = (row) => {
    navigate("/customer/profile/profile-details", {
      state: { patientId: row.id },
    });
  };

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />
        <main className={styles.content}>
          <div className={styles.filterBarWrapper}>
            <div className={styles.filterCard}>
              <FilterSearch
                filterName="Filter"
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by surname, initial, title, or address..."
              />
              <FilterButtons
                buttons={["All", "Active", "Inactive"]}
                activeButton={statusFilter}
                onButtonClick={setStatusFilter}
              />
            </div>
          </div>

          <Table columns={columns} data={paginatedData} />
          {totalPages > 1 && (
            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
    
        </main>
        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}